import type {
  AnywhereListing,
  AnywhereType,
  BuildoutListing,
  BuildoutType,
  ListingMatch,
  SaleOrLease,
  SortOrder
} from '../types';

const compareFields = (a: string, b: string, direction: string) => {
  if (!a) return 1; // null values last
  if (!b) return -1; // null values last

  if (direction === 'asc') {
    return a.toUpperCase() < b.toUpperCase() ? -1 : 1;
  } else if (direction === 'desc') {
    return a.toUpperCase() < b.toUpperCase() ? 1 : -1;
  }

  return 0; // Default case if no direction matches
};

export const sortAnywhereListings = (listings: AnywhereListing[], sortOrder: SortOrder) => {
  const { field, direction } = sortOrder;
  return listings.sort((a, b) => {
    if (field === 'address') {
      return compareFields(a.propertyLocation.addressLine1, b.propertyLocation.addressLine1, direction);
    }
    if (field === 'city') {
      return compareFields(a.propertyLocation.city, b.propertyLocation.city, direction);
    }
    if (field === 'state') {
      return compareFields(a.propertyLocation.stateProvinceName, b.propertyLocation.stateProvinceName, direction);
    }
    if (field === 'zip') {
      return compareFields(a.propertyLocation.postalCode, b.propertyLocation.postalCode, direction);
    }
    return 0;
  });
};

const filterAnywhereListings = (listings: AnywhereListing[], searchText: string, saleOrLease: SaleOrLease) => {
  const filteredBySaleOrLease = listings.filter((listing) => {
    if (saleOrLease === 'sale') return listing.typeDescription === 'Commercial Sale';
    if (saleOrLease === 'lease') return listing.typeDescription === 'Commercial Lease';
    
    return true;
  });
  if (!searchText) return filteredBySaleOrLease;

  const lowerSearchText = searchText.toLowerCase();
  return filteredBySaleOrLease.filter(listing => (
    listing.propertyLocation.addressLine1.toLowerCase().includes(lowerSearchText) ||
    listing.propertyLocation.city.toLowerCase().includes(lowerSearchText) ||
    listing.propertyLocation.stateProvinceName.toLowerCase().includes(lowerSearchText) ||
    listing.propertyLocation.postalCode.toLowerCase().includes(lowerSearchText)
  ));
};

export const sortBuildoutListings = (listings: BuildoutListing[], sortOrder: SortOrder) => {
  const { field, direction } = sortOrder;
  return listings.sort((a, b) => {
    if (field === 'address') {
      return compareFields(a.address, b.address, direction);
    }
    if (field === 'city') {
      return compareFields(a.city, b.city, direction);
    }
    if (field === 'state') {
      return compareFields(a.state, b.state, direction);
    }
    if (field === 'zip') {
      return compareFields(a.zip, b.zip, direction);
    }
    return 0;
  });
};

const filerBuildoutListings = (listings: BuildoutListing[], searchText: string, saleOrLease: SaleOrLease) => {
  const filteredBySaleOrLease = listings.filter((listing) => {
    if (saleOrLease === 'sale') return listing.class === 'SaleListing';
    if (saleOrLease === 'lease') return listing.class === 'LeaseListing';

    return true;
  });
  if (!searchText) return filteredBySaleOrLease;

  const lowerSearchText = searchText.toLowerCase();
  return filteredBySaleOrLease.filter(listing => (
    listing.address.toLowerCase().includes(lowerSearchText) ||
    listing.city.toLowerCase().includes(lowerSearchText) ||
    listing.state.toLowerCase().includes(lowerSearchText) ||
    listing.zip.toLowerCase().includes(lowerSearchText)
  ));
};

export const processListings = (
  anywhereListings: AnywhereListing[],
  buildoutListings: BuildoutListing[],
  sortOrder: SortOrder,
  searchText: string,
  saleOrLease: SaleOrLease
) => {
  const sortedAnywhereListings = sortAnywhereListings(anywhereListings, sortOrder);
  const filteredAnywhereListings = filterAnywhereListings(sortedAnywhereListings, searchText, saleOrLease);
  const matchedListings = filteredAnywhereListings.reduce<ListingMatch[]>((acc, listing) => {
    const buildoutListing = buildoutListings.find(boListing => boListing.guid === listing.listingGuid);
    if (buildoutListing) {
      const match: ListingMatch = {
        buildoutListingId: buildoutListing.id,
        anywhereListingId: listing.listingId,
        guid: listing.listingGuid,
        boListing: buildoutListing,
        anywhereListing: listing,
      };
      acc.push(match);
    }
    return acc;
  }, []);

  const unmatchedAnywhereListings = filteredAnywhereListings.filter(listing => (
    !matchedListings.some(match => match.anywhereListingId === listing.listingId)
  ));

  let unmatchedBuildoutListings = buildoutListings.filter(boListing => (
    !matchedListings.some(match => match.buildoutListingId === boListing.id)
  ));
  unmatchedBuildoutListings = sortBuildoutListings(unmatchedBuildoutListings, sortOrder);
  unmatchedBuildoutListings = filerBuildoutListings(unmatchedBuildoutListings, searchText, saleOrLease);

  return {
    matchedListings,
    unmatchedAnywhereListings,
    unmatchedBuildoutListings,
  };
};


export const buildoutTypeLabel = (type: BuildoutType): string => {
  switch (type) {
  case 'LeaseListing':
    return 'Lease';
  case 'SaleListing':
    return 'Sale';
  default:
    return type;
  }
};

export const anywhereTypeLabel = (type: AnywhereType): string => {
  switch (type) {
  case 'Commercial Lease':
    return 'Lease';
  case 'Commercial Sale':
    return 'Sale';
  default:
    return type;
  }
};
